/**
 * Sheet 41, Settings › Bureau credentials (mission 7 WP09 T049, T050; callouts
 * 1, 2, 11 and 12; FR-005, FR-007, FR-028, NFR-007, C-004, C-014; TX.1, TX.3,
 * TEN.4, A.5; contracts/ui-api.md "Sheet 41").
 *
 * Inside the settings layout (§8): the `h1`, the handling statement, the
 * processing-terms card, then one card per bureau in bureau order. The bureaus
 * and terms reads are once per navigation with no refetch on focus (NFR-008,
 * squad U16); each shows skeletons while it loads and its own failure band
 * with Try again. One identity: no role variants (C-004).
 *
 * A hash naming a bureau (`#transunion`) scrolls that card into view and
 * focuses its heading, on arrival and whenever the hash changes: sheet 32's
 * "Test connection for that bureau" links here (squad E14, P14; OR-5, OR-17).
 */
import { useEffect, type ReactNode } from 'react'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useLocation } from 'react-router-dom'

import { useTenant } from '@/components/trellis/AuthGate'
import { Band } from '@/components/trellis/Band'
import { ProblemBand } from '@/components/trellis/ProblemBand'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { ApiError } from '@/lib/api'
import { BUREAUS, type BureausResponse } from '@/lib/api/bureaus'
import { copyText } from '@/lib/provisional-copy'
import { bureausQuery, processingTermsQuery } from '@/lib/queries'
import { BureauCard } from './bureaus/BureauCard'
import { TermsCard } from './bureaus/TermsCard'
import { SettingsLayout } from './SettingsLayout'

export default function BureauCredentials() {
  const bureaus = useQuery(bureausQuery())
  const terms = useQuery(processingTermsQuery())
  const tenant = useTenant()

  return (
    <SettingsLayout>
      <h1 className="text-h1 text-navy-950">{copyText('crumb.bureaus')}</h1>
      <Card>
        <CardContent className="text-body-sm text-navy-700">
          <p>{copyText('bureauHandlingStatement')}</p>
        </CardContent>
      </Card>
      <Read query={terms} skeleton={<Skeleton className="h-64 rounded-lg" />}>
        {(data) => <TermsCard terms={data} email={tenant.data?.contact_email ?? ''} />}
      </Read>
      <Read
        query={bureaus}
        skeleton={BUREAUS.map((b) => (
          <Skeleton key={b} className="h-96 rounded-lg" />
        ))}
      >
        {(data) => <BureauCards data={data} />}
      </Read>
    </SettingsLayout>
  )
}

/**
 * One read: skeletons while pending, a failure band with Try again, else the content. A failed background
 * refetch keeps the content it already read and shows the band above it (mission 7 review follow-up 10, UI-2;
 * FR-022; TX.3, A.5): only a read that never answered replaces the cards with the band.
 */
function Read<T>({
  query,
  skeleton,
  children,
}: {
  query: UseQueryResult<T>
  skeleton: ReactNode
  children: (data: T) => ReactNode
}) {
  if (query.isPending) return <>{skeleton}</>
  const band = query.isError ? <FailureBand query={query} /> : null
  if (query.data === undefined) return band
  return (
    <>
      {band}
      {children(query.data)}
    </>
  )
}

function FailureBand<T>({ query }: { query: UseQueryResult<T> }) {
  const retry = (
    <Button variant="secondary" size="sm" loading={query.isFetching} onClick={() => void query.refetch()}>
      {copyText('retry')}
    </Button>
  )
  return query.error instanceof ApiError ? (
    <div className="flex flex-col items-start gap-2">
      <ProblemBand problem={query.error.problem} />
      {retry}
    </div>
  ) : (
    <Band kind="blocked" action={retry}>
      <p>{copyText('genericError')}</p>
    </Band>
  )
}

function BureauCards({ data }: { data: BureausResponse }) {
  const { hash } = useLocation()

  useEffect(() => {
    let target: string
    try {
      target = decodeURIComponent(hash.slice(1))
    } catch {
      return // A malformed escape (a hand-typed #%E0) names no bureau.
    }
    if (!(BUREAUS as readonly string[]).includes(target)) return
    const card = document.getElementById(target)
    card?.scrollIntoView({ block: 'start' })
    card?.querySelector<HTMLElement>('h2')?.focus()
  }, [hash])

  // Bureau order is BUREAUS order, whatever order the read answers in.
  return (
    <>
      {BUREAUS.map((bureau) => {
        const entry = data.bureaus.find((b) => b.bureau === bureau)
        return entry ? <BureauCard key={bureau} entry={entry} /> : null
      })}
    </>
  )
}
